import {ChangeDetectionStrategy, Component, inject, Input, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BaseComponent, TemplateFeature} from 'compob2b-libs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {translations} from '../../../../../../compob2b-admin/src/environments/translations.ru';
import {
  TemplateFeatureService,
  TemplateFeatureValue
} from '../../../../../../compob2b-admin/src/app/cabinet/pim/product/create-update/features/template-feature.service'

interface Size {
  width?: number;
  height?: number;
  depth?: number;
}

@Component({
  selector: 'app-tf-size',
  templateUrl: './tf-size.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfSizeComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;

  form = new UntypedFormGroup({
    width: new UntypedFormControl(null),
    height: new UntypedFormControl(null),
    depth: new UntypedFormControl(null),
  });

  private intNumberPattern = /^-?\d+$/;
  private floatNumberPattern = /^-?\d+(\.\d+)?$/;

  depthPlaceholder = translations.depth + ': ';
  widthPlaceholder = translations.width + ': ';
  heightPlaceholder = translations.height + ': ';

  templateFeatureService = inject(TemplateFeatureService);

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe)
      )
      .subscribe((data: Size) => {
        const featureData: string = this.isNotEmpty(data) ? JSON.stringify(this.parseSizeData(data)) : null;
        this.setFeature(featureData, this.form.valid, this.isShowEmptyWarning());
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setDefaultValue(): void {
    this.depthPlaceholder += this.feature.isInteger ? translations.intNumberPlaceholder.toLowerCase()
      : translations.floatNumberPlaceholder.toLowerCase();
    this.widthPlaceholder += this.feature.isInteger ? translations.intNumberPlaceholder.toLowerCase()
      : translations.floatNumberPlaceholder.toLowerCase();
    this.heightPlaceholder += this.feature.isInteger ? translations.intNumberPlaceholder.toLowerCase()
      : translations.floatNumberPlaceholder.toLowerCase();

    this.widthSize.addValidators(Validators.pattern(this.feature.isInteger ? this.intNumberPattern : this.floatNumberPattern));
    this.heightSize.addValidators(Validators.pattern(this.feature.isInteger ? this.intNumberPattern : this.floatNumberPattern));
    this.depthSize.addValidators(Validators.pattern(this.feature.isInteger ? this.intNumberPattern : this.floatNumberPattern));

    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel && featureModel[this.feature.id]) {
      const size: Size = JSON.parse(featureModel[this.feature.id].toString());
      if (size && this.isNotEmpty(size)) {
        this.form.patchValue(this.parseSizeData(size), {emitEvent: false});
        this.setDefaultFeatureValue(JSON.stringify(this.form.value), this.form.valid, this.isShowEmptyWarning());
        return;
      }
    }

    this.setDefaultFeatureValue(null, true, this.isRequired());
  }

  private isNotEmpty(size: Size): boolean {
    return this.isDimensionNotEmpty(size.width)
      || this.isDimensionNotEmpty(size.height)
      || this.isDimensionNotEmpty(size.depth);
  }

  private isDimensionNotEmpty(dimension): boolean {
    return dimension !== undefined && dimension !== null && dimension.toString().trim() !== '';
  }

  private parseSizeData(size: Size): Size {
    if (size === null || size === undefined || !this.isNotEmpty(size)) {
      return null;
    }

    if (!this.isDimensionNotEmpty(size.width)) {
      size.width = null;
    }

    if (!this.isDimensionNotEmpty(size.height)) {
      size.height = null;
    }

    if (!this.isDimensionNotEmpty(size.depth)) {
      size.depth = null;
    }

    return size;
  }

  private setFeature(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty,
      true
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue, valid: boolean, empty: boolean): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      valid,
      empty
    );
  }

  isSizeNoEmpty(): boolean {
    return this.widthSize.value && this.heightSize.value && this.depthSize.value;
  }

  isSizeEmpty(): boolean {
    return !this.isSizeNoEmpty();
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isSizeEmpty();
  }

  isPatternError(): boolean {
    return this.widthSize.hasError('pattern') || this.heightSize.hasError('pattern') || this.depthSize.hasError('pattern');
  }

  hasSizeErrors(): boolean {
    return !!this.widthSize.errors || !!this.heightSize.errors || !!this.depthSize.errors;
  }

  get widthSize(): any {
    return this.form.get('width');
  }

  get heightSize(): any {
    return this.form.get('height');
  }

  get depthSize(): any {
    return this.form.get('depth');
  }
}
